import cloneDeep from 'lodash/cloneDeep';
import xor from 'lodash/xor';
import {
  ACTIVE_BENEFIT_STATUSES,
  APPLICABLE_BENEFITS,
  APPLICABLE_NEW_BENEFITS,
  BENEFITS,
  BENEFITS_NEW,
  CONVERGENT_GROUP_FLAG_LOVE,
  CONVERGENT_GROUP_TYPE_17,
  STATUS_PROTECTION_PERIOD,
} from '../constants.js';

/**
 * @property {string} status
 * @property {string} type
 * @property {string} flag
 * @property {LoveBenefit[]} benefits
 * @property {LoveMember[]} currentMembers - currently active members of the group, unchangeable throughout customer journey
 * @property {LoveMember[]} potentialMembers - new configuration of members that customer can modify and save
 */
export default class LoveGroup {
  /**
   * @param {string} status
   * @param {string} type
   * @param {string} flag
   * @param {LoveBenefit[]} benefits
   * @param {LoveMember[]} members
   */
  constructor({ status, type, flag, benefits, members }) {
    this.status = status;
    this.type = type;
    this.flag = flag;
    this.benefits = benefits;
    this.currentMembers = cloneDeep(members);
    this.potentialMembers = cloneDeep(members);
  }

  /**
   * @returns {string[]}
   */
  get currentMemberSns() {
    return this.currentMembers.map(member => member.sn);
  }

  /**
   * @returns {string[]}
   */
  get currentMemberSubscriptionIds() {
    return this.currentMembers.map(member => member.subscriptionId);
  }

  /**
   * @returns {string[]}
   */
  get potentialMemberSubscriptionIds() {
    return this.potentialMembers.map(member => member.subscriptionId);
  }

  /**
   * @returns {boolean}
   */
  get isGroupBeingModified() {
    const groupMembersDifference = xor(
      this.currentMemberSubscriptionIds,
      this.potentialMemberSubscriptionIds,
    );
    return groupMembersDifference.length !== 0;
  }

  /**
   * @returns {boolean}
   */
  get isLove17() {
    return this.type === CONVERGENT_GROUP_TYPE_17 && this.flag === CONVERGENT_GROUP_FLAG_LOVE;
  }

  /**
   * If group is love 17 and all of its SNs are also eligible for love 17 grow,
   * the whole group itself is treated as love 17 grow group.
   *
   * @returns {boolean}
   */
  get isLove17Grow() {
    return this.isLove17 && this.currentMembers.every(member => member.isLove17GrowEligible);
  }

  /**
   * During each billing period love groups are checked whether they still comply with all of the love conditions.
   * If no, groups are either made inactive or are put into so called protection period.
   * Groups that are in this period can be checked again in next billing period whether the problem was resolved and
   * if they comply with all of the love conditions again. If so, they are made active.
   *
   * While stuck in the protection period, benefit usage is stopped.
   *
   * @returns {boolean}
   */
  get isInProtectionPeriod() {
    return this.status === STATUS_PROTECTION_PERIOD;
  }

  /**
   * Only benefits ecare is interested in are `CLAIM_GSM_2XDATA` and `CLAIM_5E_VOICE`
   * and whether these benefits are `APPLIED` (for active groups) or `WAITING_FOR_FIRST_BD`
   * (for upcoming groups)
   *
   * @returns {LoveBenefit[]}
   */
  get appliedBenefits() {
    return this.benefits.filter(
      benefit =>
        (APPLICABLE_BENEFITS.includes(benefit.id) ||
          APPLICABLE_NEW_BENEFITS.includes(benefit.id)) &&
        ACTIVE_BENEFIT_STATUSES.includes(benefit.status),
    );
  }

  /**
   * Checks whether the benefits current group has applied are old benefits, e.g. obtained before 29. 10. 2024
   *
   * @returns {boolean}
   */
  get hasOldAppliedBenefits() {
    if (!this.isLove17Grow) {
      return false;
    }
    const growTariffLevel = this.currentMembers.find(
      member => member.isGrowVoice,
    ).expectedGrowBenefitLevel;

    const oldBenefits = BENEFITS.get(growTariffLevel) ?? null;

    const oldBenefitDifference = xor(
      this.appliedBenefits.map(benefit => benefit.id),
      oldBenefits,
    );
    return oldBenefitDifference.length === 0;
  }

  /**
   * Obtained benefits of current love group.
   *
   * @returns {?('benefit-level-basic'|'benefit-level-medium'|'benefit-level-high'|'benefit-level-premium')}
   */
  get obtainedLove17GrowBenefitLevel() {
    if (!this.isLove17Grow) {
      return null;
    }
    return this.currentMembers.find(member => member.isGrowVoice).expectedGrowBenefitLevel;
  }

  /**
   *  Obtainable benefits of the love group, if customer saves it with new set of potential members.
   *
   * @returns {?('benefit-level-basic'|'benefit-level-medium'|'benefit-level-high'|'benefit-level-premium')}
   */
  get potentialLove17GrowBenefitLevel() {
    if (!this.isLove17Grow) {
      return null;
    }
    return this.potentialMembers.find(member => member.isGrowVoice).expectedGrowBenefitLevel;
  }

  /**
   * For love 17 GROW groups we evaluate whether there is any discrepancy between obtained and
   * expected benefits, as we know what benefits should given combination of VOICE and FIX have,
   * but there are some cases in which the group receives other benefits (usually it is some kind of error).
   *
   * There are two sets of benefits some tariffs may have (old or new) and we don't know which one they actually have,
   * so we have to check for both.
   *
   * Groups in protection period are excluded from this evaluation as we don't communicate their benefits in ecare
   * (since they are temporarily non-available anyway).
   *
   * @returns {boolean}
   */
  get hasObtainedAndExpectedBenefitsDiscrepancy() {
    if (!this.isLove17Grow || this.isInProtectionPeriod) {
      return false;
    }
    const benefitDifference = xor(
      this.appliedBenefits.map(benefit => benefit.id),
      BENEFITS.get(this.obtainedLove17GrowBenefitLevel),
    );
    const newBenefitDifference = xor(
      this.appliedBenefits.map(benefit => benefit.id),
      BENEFITS_NEW.get(this.obtainedLove17GrowBenefitLevel),
    );

    return benefitDifference.length !== 0 && newBenefitDifference.length !== 0;
  }

  /**
   * @param {string} subscriptionId
   * @returns {boolean}
   */
  getIsSubscriptionIdInPotentialGroup(subscriptionId) {
    return this.potentialMemberSubscriptionIds.includes(subscriptionId);
  }

  /**
   * @param {string} subscriptionId
   * @returns {boolean}
   */
  getIsSubscriptionIdInCurrentGroup(subscriptionId) {
    return this.currentMemberSubscriptionIds.includes(subscriptionId);
  }

  /**
   * Swaps new member with member of the same type (i.e. voice or internet).
   *
   * This method is ought to be used only for love 17 groups
   * which have exactly one member of each type.
   *
   * @param {LoveMember} newMember
   */
  swapMember(newMember) {
    const removedMemberIndex = this.potentialMembers.findIndex(
      groupMember => groupMember.isFixed === newMember.isFixed,
    );
    this.potentialMembers.splice(removedMemberIndex, 1, newMember);
  }
}
